/**
 * Numeric helpers.
 *
 * Every dose calculation in this app runs through `round`. Naive rounding
 * (`Math.round(n * 100) / 100`) is wrong for values that binary floating point
 * cannot represent exactly: `1.005 * 100` is `100.49999999999999`, so the naive
 * form yields `1` instead of `1.01`. Routing through `toPrecision(15)` first
 * collapses that representation error before the rounding decision is made.
 *
 * 15 significant digits is the largest precision at which every IEEE-754 double
 * round-trips unambiguously, so this never invents precision that is not there.
 */

/** Round to `dp` decimal places, correcting for binary representation error. */
export function round(value: number, dp = 6): number {
  if (!Number.isFinite(value)) return NaN;
  const factor = 10 ** dp;
  return Math.round(Number((value * factor).toPrecision(15))) / factor;
}

/** Round down to the nearest multiple of `step` (step must be > 0). */
export function floorToStep(value: number, step: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return NaN;
  return round(Math.floor(round(value / step, 9)) * step, 9);
}

/** Round to the nearest multiple of `step`, halves away from zero. */
export function roundToStep(value: number, step: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return NaN;
  return round(Math.round(round(value / step, 9)) * step, 9);
}

/** True when two floats agree to within `tolerance`. */
export function nearly(a: number, b: number, tolerance = 1e-9): boolean {
  return Math.abs(a - b) <= tolerance;
}

/** Finite and strictly greater than zero. */
export function isPositive(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0;
}

/** Finite and zero or greater. */
export function isNonNegative(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0;
}

/** Clamp into [min, max]. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
